import { LitElement, html, unsafeCSS, CSSResult  } from 'lit';
import { property, query } from 'lit/decorators.js';
import styles from './dialog.scss?inline';
import bodyStyles from './dialog.body.scss?inline';
import slotStyles from './dialog.slot.scss?inline';
import { appendStylesheet } from '../core/utils';
import { IconButtonComponent } from '../icon-button/icon-button';
import { IconComponent } from '../icon/icon';
import { iconService } from '../icon/icon.service';

IconComponent.define();
IconButtonComponent.define();
iconService.importStickersheet();

export type DialogType = 'modal' | 'non-modal';
export type DialogVariant = 'basic' | 'success' | 'error' | 'warning' | 'drawer';

export class DialogComponent extends LitElement {
  static selector = `{{c11n-prefix}}-dialog`;
  static override styles: CSSResult = unsafeCSS(styles);
  static define = () => {
    const isDeclared = customElements.get(DialogComponent.selector);
    if (!isDeclared) {
      customElements.define(DialogComponent.selector, DialogComponent);
    }
  };

  maxVerticalMargin: number = 128;
  dialogHeight!: number;
  isBeforeElIntersecting!: boolean;
  isAfterElIntersecting!: boolean;
  resizeObserver!: ResizeObserver;
  intersectionObserver!: IntersectionObserver;
  clickStartTarget!: EventTarget;
  vh!: string;

  @query('dialog.c11n-dialog')
  public dialogEl!: HTMLDialogElement;

  @query('.c11n-dialog__body__content__before')
  beforeEl!: HTMLElement;

  @query('.c11n-dialog__body__content__after')
  afterEl!: HTMLElement;

  // Some attributes (type, variant) were being stripped in Stencil apps,
  // but we found a simple workaround. If using @property 
  // we may need to differentiate the name from the attribute 
  // https://github.com/ionic-team/stencil/issues/2703

  @property({ attribute: 'dialog-label', type: String })
  dialogLabel: string = 'Please provide a label';

  @property({ attribute: 'type', type: String })
  _type: DialogType = 'modal';

  @property({ attribute: 'variant', type: String })
  _variant: DialogVariant = 'basic';

  @property({ attribute: 'c11n-web-dialog', type: Boolean, reflect: true })
  readonly _c11n_web_dialog: boolean = true;

  handleTransitionEnd = () => {
    this.dialogEl.close();
    this.dialogEl.removeEventListener('transitionend', this.handleTransitionEnd);
    document.body.classList.remove('c11n-dialog--modal-open');
  };

  handleClickStart = (event: MouseEvent | TouchEvent) => {
    if (event.target) {
      this.clickStartTarget = event.target;
    }
  };

  handleCancelEvent = (event: Event) => {
    event.preventDefault();
  };

  handleBackdropClick = (event: PointerEvent) => {
    if (this._type === 'modal' && event.target === this.clickStartTarget) {
      const rect = this.dialogEl.getBoundingClientRect();
      const isInDialog = (
        rect.top <= event.clientY && 
        event.clientY <= rect.top + rect.height && 
        rect.left <= event.clientX && 
        event.clientX <= rect.left + rect.width
      );
      if (!isInDialog) {
        this.closeDialog();
      }
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this._type === 'modal') {
      this.closeDialog();
    }
  };

  handleWindowResize = () => {
    if (this.dialogEl.open) {
      this.setVHAndAutoMargin(window.innerHeight);
    }
  };

  setVHAndAutoMargin = (viewportHeight: number) => {
    this.setVH(viewportHeight);
    this.setAutoMargin(viewportHeight);
  };

  setAutoMargin = (viewportHeight: number) => {
    if (viewportHeight - this.maxVerticalMargin * 2 < this.dialogHeight) {
      this.dialogEl.classList.add('c11n-dialog--auto-margin');
    } else {
      this.dialogEl.classList.remove('c11n-dialog--auto-margin');
    }
  };

  setVH = (viewportHeight: number) => {
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    // On mobile browsers, the value of vh is fixed based on the max height of the screen
    // hence, we can't get the full height of the screen when UI like search is in the view.
    // So, we're calculating the value of vh based on the actual height that is available
    // whether UI in the mobile browser is in the view or not.
    const vh = viewportHeight * 0.01;
    const oldVHValue = this.vh;
    this.vh = `${vh}px`;
    this.requestUpdate('style', oldVHValue);
  }

  handleConditionalBorder = () => {
    if (this.isBeforeElIntersecting && this.isAfterElIntersecting) {
      this.dialogEl.classList.remove('c11n-dialog--has-overflow');
    } else {
      this.dialogEl.classList.add('c11n-dialog--has-overflow');
    }
  };

  public openDialog = () => {
    if (this._type === 'modal') {
      /**
       * TODO:
       * Consider how class is added to document body from within the component
       * but the styles need to be global
       */
      document.body.classList.add('c11n-dialog--modal-open');
      this.setVH(window.innerHeight);
      window.addEventListener('resize', this.handleWindowResize);
      document.addEventListener('keydown', this.handleKeyDown);
      this.dialogEl.showModal();
      /**
       * Wait to enable transition in Safari
       */
      window.requestAnimationFrame(() => {
        this.dialogEl.classList.add('c11n-dialog--open');
        /**
         * Ensure setAutoMargin method called after dialog resize in Safari
         */
        window.requestAnimationFrame(() => {
          this.setAutoMargin(window.innerHeight);
        });
      });
    } else {
      console.log('This is not a modal dialog');
    }
  };

  public closeDialog = () => {
    if (this._type === 'modal') {
      window.removeEventListener('resize', this.handleWindowResize);
      document.removeEventListener('keydown', this.handleKeyDown);
      this.dialogEl.addEventListener('transitionend', this.handleTransitionEnd);
      this.dialogEl.classList.remove('c11n-dialog--open');
    }
  };

  addObservers = () => {
    this.resizeObserver = new ResizeObserver((entries) => {
      if (this.dialogEl.open) {
        for (const entry of entries) {
          switch(entry.target) {
            case this.dialogEl:
              this.dialogHeight = entry.target.getBoundingClientRect().height;
              break;
          }
        }
      }
    });
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (this.dialogEl.open) {
        for (const entry of entries) {
          switch(entry.target) {
            case this.beforeEl:
              this.isBeforeElIntersecting = entry.isIntersecting;
              break;
            case this.afterEl:
              this.isAfterElIntersecting = entry.isIntersecting;
              break;
          }
        }
        setTimeout(() => this.handleConditionalBorder());
      }
    });
    this.resizeObserver.observe(this.dialogEl);
    this.intersectionObserver.observe(this.beforeEl);
    this.intersectionObserver.observe(this.afterEl);
  };

  removeObservers = () => {
    this.resizeObserver.unobserve(this.dialogEl);
    this.intersectionObserver.unobserve(this.beforeEl);
    this.intersectionObserver.unobserve(this.afterEl);
  };

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();
    setTimeout(() => this.addObservers());
  }


  override disconnectedCallback() {
    super.disconnectedCallback();
    /**
     * removing listeners added in case dialog is open when disconnected
     */
    if (this.dialogEl.open) {
      this.dialogEl.close();
      document.body.classList.remove('c11n-dialog--modal-open');
      this.dialogEl.classList.remove('c11n-dialog--open');
      document.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('resize', this.handleWindowResize);
    }
    this.removeObservers();
  }

  appendStyles() {
    const slotStylesheet: string = slotStyles.toString()
      .replaceAll(/\bc11n-web-dialog\b/g, this.tagName.toLowerCase());
    appendStylesheet({ 
      target: this, 
      styles: slotStylesheet, 
      name: `${this.tagName.toLowerCase()}-slot`
    });
    appendStylesheet({ 
      target: document.body, 
      styles: bodyStyles.toString(),
      name: `${this.tagName.toLowerCase()}-body`
    });
  }

  getIconMarkup = () => {
    if (this._variant !== 'basic') {
      return html`<{{c11n-prefix}}-icon class="c11n-dialog__icon" name="${this._variant}-fill" size="medium" alt-text="${this._variant}"></{{c11n-prefix}}-icon>`;
    }
    return html``;
  };

  override render() {
    return html`
    <dialog class="c11n-dialog" aria-label="${this.dialogLabel}" @click="${this.handleBackdropClick}" @mousedown="${this.handleClickStart}" @touchstart="${this.handleClickStart}" @cancel="${this.handleCancelEvent}" style="--vh: ${this.vh};">
      <{{c11n-prefix}}-icon-button class="c11n-dialog__close-button" size="small" variant="ghost" @click="${this.closeDialog}">
        <button aria-label="Close modal dialog" title="Close modal dialog">
          <{{c11n-prefix}}-icon name="remove"></{{c11n-prefix}}-icon>
        </button>
      </{{c11n-prefix}}-icon-button>
      <header class="c11n-dialog__header">
        <div class="c11n-dialog__header__content">
          <slot name="header"></slot>
        </div>
      </header>
      <div class="c11n-dialog__body">
        <div class="c11n-dialog__body__content">
          <div class="c11n-dialog__body__content__before"></div>
          <slot name="body"></slot>
          <div class="c11n-dialog__body__content__after"></div>
        </div>
      </div>
      <footer class="c11n-dialog__footer">
        <div class="c11n-dialog__footer__content">
          <slot name="footer"></slot>
        </div>
      </footer>
      ${this.getIconMarkup()}
    </dialog>
    `;
  }
}

export const C11nDialog = DialogComponent.selector;
